import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { paint, register, unregister } from './engine';
import { assertMeshPalette, toMeshColorsKey } from './palette';
const MOTION_INTENSITY = 1.5;
const MOTION_SPEED = 4;
const MOTION_PAN = 0.7;
const MOTION_ROTATE = 0.5;
const MOTION_RAMP_SEC = 0.8;
const SurfaceCanvas = styled.canvas`
  width: ${({ $width })=>$width}px;
  height: ${({ $height })=>$height}px;
  flex-shrink: 0;
  border-radius: ${({ $borderRadius })=>$borderRadius};
  display: block;
`;
function prefersReducedMotion() {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
export const MeshSurface = ({ colors, meshId, width, height, borderRadius = '0', animated = false, className, 'data-cy': dataCy })=>{
    const canvasRef = useRef(null);
    const colorsKey = toMeshColorsKey(colors);
    const wantMotionRef = useRef(false);
    const ensureRunningRef = useRef(()=>{});
    wantMotionRef.current = Boolean(animated) && !prefersReducedMotion();
    useEffect(()=>{
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }
        assertMeshPalette(colorsKey.split(','));
        let cancelled = false;
        let env = 0;
        let t0 = null;
        let last = performance.now();
        let frame = null;
        const buildCfg = (ampMod, freqMod, panX, rot)=>({
                colors: colorsKey,
                meshId,
                ampMod,
                freqMod,
                panX,
                rot,
                target: canvas
            });
        void paint(canvas, buildCfg(0, [
            0,
            0
        ], 0, 0)).catch((error)=>{
            if (!cancelled) {
                // eslint-disable-next-line no-console
                console.warn('MeshSurface paint failed', error);
            }
        });
        const ensureRunning = ()=>{
            if (frame || cancelled) {
                return;
            }
            if (t0 == null) {
                t0 = performance.now();
            }
            last = performance.now();
            frame = ()=>{
                if (cancelled || !frame) {
                    return null;
                }
                const target = wantMotionRef.current ? 1 : 0;
                const now = performance.now();
                const dt = Math.min(0.05, (now - last) / 1000);
                last = now;
                env = target > env ? Math.min(1, env + dt / MOTION_RAMP_SEC) : Math.max(0, env - dt / MOTION_RAMP_SEC);
                if (env === 0 && target === 0) {
                    const f = frame;
                    frame = null;
                    t0 = null;
                    setTimeout(()=>unregister(f), 0);
                    return buildCfg(0, [
                        0,
                        0
                    ], 0, 0);
                }
                const e = env * env * (3 - 2 * env);
                const depth = MOTION_INTENSITY * e;
                const t = (now - (t0 !== null && t0 !== void 0 ? t0 : now)) / 1000 * MOTION_SPEED;
                const ampSin = Math.sin(t * 1.1);
                return buildCfg(depth * (ampSin > 0 ? ampSin : ampSin * 0.35), [
                    depth * 0.5 * Math.sin(t * 0.7),
                    depth * 0.5 * Math.sin(t * 0.53)
                ], e * MOTION_PAN * Math.sin(t * 0.31), e * MOTION_ROTATE * Math.PI * Math.sin(t * 0.23));
            };
            void register(frame, colorsKey).catch((error)=>{
                if (!cancelled) {
                    // eslint-disable-next-line no-console
                    console.warn('MeshSurface animate failed', error);
                }
            });
        };
        ensureRunningRef.current = ensureRunning;
        if (wantMotionRef.current) {
            ensureRunning();
        }
        return ()=>{
            cancelled = true;
            ensureRunningRef.current = ()=>{};
            if (frame) {
                unregister(frame);
                frame = null;
            }
        };
    }, [
        colorsKey,
        meshId,
        width,
        height
    ]);
    useEffect(()=>{
        if (wantMotionRef.current) {
            ensureRunningRef.current();
        }
    }, [
        animated
    ]);
    return /*#__PURE__*/ _jsx(SurfaceCanvas, {
        ref: canvasRef,
        $width: width,
        $height: height,
        $borderRadius: borderRadius,
        className: className,
        "data-cy": dataCy,
        width: width * 2,
        height: height * 2,
        "aria-hidden": true
    });
};
